/**
 * Self-Cert Individual Models
 * Type-safe models for Self-Cert Individual form data handling, validation, and XML export
 */

import { generateSelfCertIndividualXML } from "../components/SendingForms/utils/generateSelfCertIndividualXML";

// API Response Model - Complete form fields from API
export class SelfCertIndividualResponseModel {
  // Dynamic fields support
  [key: string]: any;

  // Core Form Fields
  "Form_name": string = "Self-Cert Individual";
  "UniqueIdentifier": string = "";

  // Individual Information
  "SC-Individual-Title": string = "";
  "SC-Individual-Last_name": string = "";
  "SC-Individual-First_name": string = "";
  "SC-Individual-Middle_name": string = "";
  "SC-Individual-City_of_birth": string = "";
  "SC-Individual-Country_of_birth": string = "";
  "SC-Individual-Date_of_birth": string = "";

  // Current Residence Address
  "SC-Individual-Current_residence_address_street": string = "";
  "SC-Individual-Current_residence_address_city": string = "";
  "SC-Individual-Current_residence_address_state": string = "";
  "SC-Individual-Current_residence_address_country": string = "";
  "SC-Individual-Current_residence_address_postal": string = "";

  // Mailing Address
  "SC-Individual-Mailing_address_street": string = "";
  "SC-Individual-Mailing_address_city": string = "";
  "SC-Individual-Mailing_address_state": string = "";
  "SC-Individual-Mailing_address_country": string = "";
  "SC-Individual-Mailing_address_postal": string = "";

  // Tax Information
  "SC-Individual-Country_tax_residence": string = "";
  "SC-Individual-Sole_residency_yes": boolean = false;
  "SC-Individual-Sole_residency_no": boolean = false;

  // Additional Tax Countries (up to 3)
  "SC-Individual-Additional_tax_country_1": string = "";
  "SC-Individual-Additional_TIN_1": string = "";
  "SC-Individual-Additional_TIN_type_1": string = "";
  "SC-Individual-Additional_tax_country_2": string = "";
  "SC-Individual-Additional_TIN_2": string = "";
  "SC-Individual-Additional_TIN_type_2": string = "";
  "SC-Individual-Additional_tax_country_3": string = "";
  "SC-Individual-Additional_TIN_3": string = "";
  "SC-Individual-Additional_TIN_type_3": string = "";
  "SC-Individual-Tax_reference_number_type": string = "";

  // Signature Information
  "SC-Individual-Signature": string = "";
  "SC-Individual-Print_name": string = "";
  "SC-Individual-Date": string = "";
  "SC-Individual-Signature_unreadable": boolean = false;
  "SC-Individual-isSigned": string = "no";
  "SC-Individual-isModified": string = "no";

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }
}

export class SelfCertIndividualFormModel {
  // Dynamic fields support
  [key: string]: any;

  // Core Form Fields
  "Form_name": string = "Self-Cert Individual";
  "UniqueIdentifier": string = "";

  // Individual Information
  "SC-Individual-Title": string = "";
  "SC-Individual-Last_name": string = "";
  "SC-Individual-First_name": string = "";
  "SC-Individual-Middle_name": string = "";
  "SC-Individual-City_of_birth": string = "";
  "SC-Individual-Country_of_birth": string = "";
  "SC-Individual-Date_of_birth": string = "";

  // Current Residence Address
  "SC-Individual-Current_residence_address_street": string = "";
  "SC-Individual-Current_residence_address_city": string = "";
  "SC-Individual-Current_residence_address_state": string = "";
  "SC-Individual-Current_residence_address_country": string = "";
  "SC-Individual-Current_residence_address_postal": string = "";

  // Mailing Address
  "SC-Individual-Mailing_address_street": string = "";
  "SC-Individual-Mailing_address_city": string = "";
  "SC-Individual-Mailing_address_state": string = "";
  "SC-Individual-Mailing_address_country": string = "";
  "SC-Individual-Mailing_address_postal": string = "";

  // Tax Information
  "SC-Individual-Country_tax_residence": string = "";
  "SC-Individual-Sole_residency_yes": boolean = false;
  "SC-Individual-Sole_residency_no": boolean = false;

  // Additional Tax Countries (up to 3)
  "SC-Individual-Additional_tax_country_1": string = "";
  "SC-Individual-Additional_TIN_1": string = "";
  "SC-Individual-Additional_TIN_type_1": string = "";
  "SC-Individual-Additional_tax_country_2": string = "";
  "SC-Individual-Additional_TIN_2": string = "";
  "SC-Individual-Additional_TIN_type_2": string = "";
  "SC-Individual-Additional_tax_country_3": string = "";
  "SC-Individual-Additional_TIN_3": string = "";
  "SC-Individual-Additional_TIN_type_3": string = "";

  // Signature Information
  "SC-Individual-Signature": string = "";
  "SC-Individual-Print_name": string = "";
  "SC-Individual-Date": string = "";
  "SC-Individual-Signature_unreadable": boolean = false;
  "SC-Individual-isSigned": string = "no";
  "SC-Individual-isModified": string = "no";

  // Additional jurisdictions fields (for table data mapping)
  "SC-Individual-Tax_residency_country": string = "";
  "SC-Individual-Tax_reference_number_type": string = "";
  "SC-Individual-Tax_id": string = "";
  "SC-Individual-Tin_or_functional_equivalent": boolean = false;
  "SC-Individual-Reason_for_non_tin": string = "";

  "sc_cp Jurisdiction 2": string = "";
  "sc_cp Tax Identification Number 2": string = "";
  "sc_cp Tax Reference Number Type 2": string = "";
  "sc_cp Reason for non-availability of TIN 2": string = "";
  "sc_cp Jurisdiction 3": string = "";
  "sc_cp Tax Identification Number 3": string = "";
  "sc_cp Tax Reference Number Type 3": string = "";
  "sc_cp Reason for non-availability of TIN 3": string = "";
  "sc_cp Jurisdiction 4": string = "";
  "sc_cp Tax Identification Number 4": string = "";
  "sc_cp Tax Reference Number Type 4": string = "";
  "sc_cp Reason for non-availability of TIN 4": string = "";

  // Mapping control flag
  "isMapDone": boolean = false;

  constructor(data?: Partial<SelfCertIndividualFormModel>) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Generate XML representation using the Self-Cert Individual XML format
   */
  async toXML(): Promise<string> {
    // Map model data to the format expected by XML generator
    const xmlData = {
      // Map form fields to XML field names

      // Include all other fields from the model
      ...this,
    };

    return await generateSelfCertIndividualXML(xmlData);
  }

  /**
   * Convert to API payload format
   */
  toApiPayload(): Record<string, any> {
    return {
      ...this,
    };
  }

  /**
   * Validate the form data
   */
  validate(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required field validations
    if (!this["SC-Individual-First_name"]) {
      errors.push("First name is required");
    }
    if (!this["SC-Individual-Last_name"]) {
      errors.push("Last name is required");
    }
    if (!this["SC-Individual-Country_tax_residence"]) {
      errors.push("Country of tax residence is required");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

/**
 * Self-Cert Individual Field Mapper - Maps response model fields to form model fields
 */
export class SelfCertIndividualFieldMapper {
  /**
   * Map response model to form model
   */
  static mapResponseToForm(
    responseModel: SelfCertIndividualResponseModel
  ): SelfCertIndividualFormModel {
    const formModel = new SelfCertIndividualFormModel();

    // Check if mapping is already done
    if (responseModel["isMapDone"]) {
      // If already mapped, just return the existing data as form model
      Object.assign(formModel, responseModel);
      return formModel;
    }

    console.log(
      "🔍 SelfCertIndividual: First time mapping - isMapDone not set, proceeding with table mapping"
    );

    // Direct field mappings
    const directMappings = [
      "UniqueIdentifier",
      "entityUniqueIdentifier",
      "Form_name",
      "SC-Individual-Title",
      "SC-Individual-Last_name",
      "SC-Individual-First_name",
      "SC-Individual-Middle_name",
      "SC-Individual-City_of_birth",
      "SC-Individual-Country_of_birth",
      "SC-Individual-Date_of_birth",
      "SC-Individual-Current_residence_address_street",
      "SC-Individual-Current_residence_address_city",
      "SC-Individual-Current_residence_address_state",
      "SC-Individual-Current_residence_address_country",
      "SC-Individual-Current_residence_address_postal",
      "SC-Individual-Mailing_address_street",
      "SC-Individual-Mailing_address_city",
      "SC-Individual-Mailing_address_state",
      "SC-Individual-Mailing_address_country",
      "SC-Individual-Mailing_address_postal",
      "SC-Individual-Country_tax_residence",
      "SC-Individual-Sole_residency_yes",
      "SC-Individual-Sole_residency_no",
      "SC-Individual-Additional_tax_country_1",
      "SC-Individual-Additional_TIN_1",
      "SC-Individual-Additional_TIN_type_1",
      "SC-Individual-Additional_tax_country_2",
      "SC-Individual-Additional_TIN_2",
      "SC-Individual-Additional_TIN_type_2",
      "SC-Individual-Additional_tax_country_3",
      "SC-Individual-Additional_TIN_3",
      "SC-Individual-Additional_TIN_type_3",
      "SC-Individual-Signature",
      "SC-Individual-Print_name",
      "SC-Individual-Date",
      "SC-Individual-Signature_unreadable",
      "SC-Individual-isSigned",
      "SC-Individual-isModified",
    ];

    directMappings.forEach((field) => {
      if (responseModel[field] !== undefined) {
        formModel[field] = responseModel[field];
      }
    });

    // Special mappings
    this.mapSpecialFields(responseModel, formModel);

    // Auto-set isSigned and Signature_unreadable based on Signature field (only during initial mapping)
    if (
      formModel["SC-Individual-Signature"] &&
      formModel["SC-Individual-Signature"].trim() !== "" &&
      !formModel.isMapDone
    ) {
      formModel["SC-Individual-isSigned"] = "yes";
      formModel["SC-Individual-Signature_unreadable"] = true;
    }

    // Set isMapDone to true to prevent re-mapping
    formModel.isMapDone = true;

    return formModel;
  }

  /**
   * Handle special field mappings and transformations
   */
  private static mapSpecialFields(
    responseModel: SelfCertIndividualResponseModel,
    formModel: SelfCertIndividualFormModel
  ): void {
    // Map API fields (hyphen format) to form fields (underscore format)
    const fieldMappings = {
      "SC-Individual-Title": "SC-Individual-Title",
      "SC-Individual-Last_name": "SC-Individual-Last_name",
      "SC-Individual-First_name": "SC-Individual-First_name",
      "SC-Individual-Middle_name": "SC-Individual-Middle_name",
      "SC-Individual-City_of_birth": "SC-Individual-City_of_birth",
      "SC-Individual-Country_of_birth": "SC-Individual-Country_of_birth",
      "SC-Individual-Date_of_birth": "SC-Individual-Date_of_birth",
      "SC-Individual-Current_residence_address_street":
        "SC-Individual-Current_residence_address_street",
      "SC-Individual-Current_residence_address_city":
        "SC-Individual-Current_residence_address_city",
      "SC-Individual-Current_residence_address_state":
        "SC-Individual-Current_residence_address_state",
      "SC-Individual-Current_residence_address_country":
        "SC-Individual-Current_residence_address_country",
      "SC-Individual-Current_residence_address_postal":
        "SC-Individual-Current_residence_address_postal",
      "SC-Individual-Mailing_address_street": "SC-Individual-Mailing_address_street",
      "SC-Individual-Mailing_address_city": "SC-Individual-Mailing_address_city",
      "SC-Individual-Mailing_address_state": "SC-Individual-Mailing_address_state",
      "SC-Individual-Mailing_address_country": "SC-Individual-Mailing_address_country",
      "SC-Individual-Mailing_address_postal": "SC-Individual-Mailing_address_postal",
      "SC-Individual-Tax_residency_country": "SC-Individual-Country_tax_residence",
      "SC-Individual-Sole_residency_yes": "SC-Individual-Sole_residency_yes",
      "SC-Individual-Sole_residency_no": "SC-Individual-Sole_residency_no",

      "SC-Individual-Signature": "SC-Individual-Signature",
      "SC-Individual-Signer_printed_name": "SC-Individual-Print_name",
      "SC-Individual-Sign_date": "SC-Individual-Date",
      "SC-Individual-Capacity": "SC-Individual-Capacity",
    };

    Object.entries(fieldMappings).forEach(([apiField, formField]) => {
      if (responseModel[apiField] !== undefined) {
        // Convert "selected" strings to boolean true for checkbox fields
        if (responseModel[apiField] === "selected") {
          formModel[formField] = true;
          console.log(
            `SelfCertIndividual mapping: ${apiField} → ${formField} = true (converted from "selected")`
          );
        } else {
          formModel[formField] = responseModel[apiField];
          console.log(
            `SelfCertIndividual mapping: ${apiField} → ${formField} = ${responseModel[apiField]}`
          );
        }
      }
    });

    // so that Jurisdiction 1 and related inputs display the mapped values.
    // if (formModel["SC-Individual-Country_tax_residence"] !== undefined) {
    //   formModel["SC-Individual-Tax_residency_country"] = formModel["SC-Individual-Country_tax_residence"];
    // }

    // // Mirror sole residency values to UI field names used by the form
    // if (formModel["SC-Individual-Sole_residency_yes"] !== undefined) {
    //   formModel["SC-Individual-Sole_residency_yes"] = !!formModel["SC-Individual-Sole_residency_yes"];
    // }
    // if (formModel["SC-Individual-Sole_residency_no"] !== undefined) {
    //   formModel["SC-Individual-Sole_residency_no"] = !!formModel["SC-Individual-Sole_residency_no"];
    // }

    let tableData = null;

    // Check for table data in different locations - prioritize new tax_purposes structure
    if (responseModel["tables"] && responseModel["tables"]["tax_purposes"]) {
      tableData = responseModel["tables"]["tax_purposes"];
      console.log("✅ SelfCertIndividual table data from tables.tax_purposes:", tableData);
    } else {
      console.log("❌ SelfCertIndividual: No table data found in responseModel");
      console.log("🔍 SelfCertIndividual: Full responseModel structure:", responseModel);
    }

    // Helper: normalize a string (lowercase, remove non-alphanumerics)
    const normalize = (s: string) =>
      (s || "")
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");
    // Helper: find value in a row by matching any of the candidate headers loosely
    const getByHeader = (row: any, candidates: string[]): any => {
      const keys = Object.keys(row || {});
      for (const k of keys) {
        const nk = normalize(k);
        for (const cand of candidates) {
          if (nk.includes(normalize(cand))) {
            return row[k];
          }
        }
      }
      return undefined;
    };

    if (tableData && Array.isArray(tableData)) {
      console.log(`✅ SelfCertIndividual: Processing ${tableData.length} table rows`);
      tableData.forEach((row: any, index: number) => {
        const jurisdictionNum = index + 2; // Start from 2 (Jurisdiction 2, 3, 4)
        console.log(
          `🔍 SelfCertIndividual: Processing row ${index} for jurisdiction ${jurisdictionNum}:`,
          row
        );

        // New direct mapping for tax_purposes structure
        if (row.tax_residence !== undefined) {
          formModel[`sc_cp Jurisdiction ${jurisdictionNum}`] = row.tax_residence;
          console.log(
            `✅ SelfCertIndividual table mapping: Jurisdiction ${jurisdictionNum} = ${row.tax_residence}`
          );
        }

        if (row.tax_id !== undefined) {
          formModel[`sc_cp Tax Identification Number ${jurisdictionNum}`] = row.tax_id;
          console.log(
            `✅ SelfCertIndividual table mapping: Tax ID ${jurisdictionNum} = ${row.tax_id}`
          );
        }

        if (row.tax_reference_number_type !== undefined) {
          formModel[`sc_cp Tax Reference Number Type ${jurisdictionNum}`] =
            row.tax_reference_number_type;
          console.log(
            `✅ SelfCertIndividual table mapping: Tax Ref Type ${jurisdictionNum} = ${row.tax_reference_number_type}`
          );
        }

        if (row.reason_for_non_tin !== undefined) {
          formModel[`sc_cp Reason for non-availability of TIN ${jurisdictionNum}`] =
            row.reason_for_non_tin;
          console.log(
            `✅ SelfCertIndividual table mapping: Reason ${jurisdictionNum} = ${row.reason_for_non_tin}`
          );
        }

        // Fallback to old table mapping logic for backward compatibility
        const jurVal = getByHeader(row, ["Jurisdiction of Tax Residence", "Jurisdiction"]);
        if (
          jurVal !== undefined &&
          formModel[`sc_cp Jurisdiction ${jurisdictionNum}`] === undefined
        ) {
          formModel[`sc_cp Jurisdiction ${jurisdictionNum}`] = jurVal;
          console.log(
            `✅ SelfCertIndividual table mapping (fallback): Jurisdiction ${jurisdictionNum} = ${jurVal}`
          );
        }

        const tinVal = getByHeader(row, ["Tax Identification Number", "Tax ID"]);
        if (
          tinVal !== undefined &&
          formModel[`sc_cp Tax Identification Number ${jurisdictionNum}`] === undefined
        ) {
          formModel[`sc_cp Tax Identification Number ${jurisdictionNum}`] = tinVal;
          console.log(
            `✅ SelfCertIndividual table mapping (fallback): Tax ID ${jurisdictionNum} = ${tinVal}`
          );
        }

        const refTypeVal = getByHeader(row, ["Tax Reference Number Type", "Reference Number Type"]);
        if (
          refTypeVal !== undefined &&
          formModel[`sc_cp Tax Reference Number Type ${jurisdictionNum}`] === undefined
        ) {
          formModel[`sc_cp Tax Reference Number Type ${jurisdictionNum}`] = refTypeVal;
          console.log(
            `✅ SelfCertIndividual table mapping (fallback): Tax Ref Type ${jurisdictionNum} = ${refTypeVal}`
          );
        }

        const reasonVal = getByHeader(row, [
          "Reason for non-availability of TIN (if applicable)",
          "Reason for non-availability",
        ]);
        if (
          reasonVal !== undefined &&
          formModel[`sc_cp Reason for non-availability of TIN ${jurisdictionNum}`] === undefined
        ) {
          formModel[`sc_cp Reason for non-availability of TIN ${jurisdictionNum}`] = reasonVal;
        }
      });
      console.log("✅ SelfCertIndividual: Table data mapping completed");
    } else {
      console.log("❌ SelfCertIndividual: No valid table data to process");
    }

    // Ensure radios default to 'no' if not provided
    if (!formModel["SC-Individual-isSigned"]) {
      formModel["SC-Individual-isSigned"] = "no";
    }
    if (!formModel["SC-Individual-isModified"]) {
      formModel["SC-Individual-isModified"] = "no";
    }

    // Map additional response fields to form fields
    Object.keys(responseModel).forEach((responseField) => {
      const value = responseModel[responseField];
      if (value !== undefined && value !== null && value !== "") {
        // Direct assignment for fields that exist in form model
        if ((formModel as any)[responseField] !== undefined && responseField !== "isSigned") {
          (formModel as any)[responseField] = value;
        }
      }
    });
  }
}
